import { Component, ViewEncapsulation, OnDestroy } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { Subscription } from 'rxjs';
import { MatDividerModule } from '@angular/material/divider';
import { MatExpansionModule } from '@angular/material/expansion';
import { MatIconModule } from '@angular/material/icon';
import { MatButtonModule } from '@angular/material/button';
import { MatRippleModule } from '@angular/material/core';
import { TranslateModule } from '@ngx-translate/core';
import { NgIf, NgFor } from '@angular/common';

import { pageTransitions } from '../../../shared/animations/page-transitions';
import { DocumentService } from '../../services/documents.service';
import { UNDERSCORE, PERCENTAGE, EMPTYSPACE } from '../../../shared/utils/constant';
import { Document, DocumentLine, FieldOfDocument } from '../../models/document';
import { UserService } from '../../../shared/services/user.service';
import { SafeUrlPipe } from '../../../shared/pipes/safeurl.pipe';
import { NumberPipe } from '../../../shared/pipes/number.pipe';
import { DatePipe } from '../../../shared/pipes/date.pipe';
import { LoadingSpinnerComponent } from '../../../shared/components/utils/loading-spinner.component';
import { OfflineComponent } from '../../../shared/components/offline/offline.component';
import { AfterViewInitDirective } from '../../../shared/directives/after-view-init.directive';
import { BackButtonComponent } from '../../../shared/components/back-button/back-button.component';
import { ScrollerDirective } from '../../../shared/directives/scroller.directive';
import { DbAnalyticsService } from '../../../shared/services/db-analytics.service';

@Component({
	selector: 'copo-order-acknowledgment-card',
	templateUrl: './order-acknowledgment-card.component.html',
	animations: pageTransitions,
	encapsulation: ViewEncapsulation.Emulated,
	imports: [
		ScrollerDirective,
		NgIf,
		BackButtonComponent,
		TranslateModule,
		MatRippleModule,

		MatButtonModule,
		MatIconModule,
		AfterViewInitDirective,
		MatExpansionModule,
		MatDividerModule,
		NgFor,
		OfflineComponent,
		LoadingSpinnerComponent,
		DatePipe,
		NumberPipe,
		SafeUrlPipe
	]
})
export class OrderAcknowledgmentCardComponent implements OnDestroy {
	orderAckFields: readonly FieldOfDocument[] = [
		'id',
		'type',
		'subtype',
		'date',
		'name',
		'paymentDueDate',
		'supplierVatNumber',
		'payerName',
		'payerVatNumber',
		'documentLines',
		'soldtoName',
		'shiptoName',
		'orderAcknowledgementDate',
		'customerOrderReference',
		'supplierOrderReference'
	];

	orderAckData?: Document;
	id: number;
	orderAckDisplayed = false;
	orderAckSub: Subscription;
	isLoading = false;
	isOffline = false;
	isDocumentNotFound = false;
	readonly UNDERSCORE = UNDERSCORE;
	readonly PERCENTAGE = PERCENTAGE;
	readonly EMPTYSPACE = EMPTYSPACE;
	constructor(
		private readonly documentService: DocumentService,
		private readonly route: ActivatedRoute,
		private readonly userService: UserService,
		private readonly dbAnalyticsService: DbAnalyticsService,
	) {
		this.id = <number> this.route.snapshot.params['id'];
		this.isOffline = !navigator.onLine;
		const { user } = this.userService;
		this.orderAckDisplayed = user.hasServiceParameterEqualsTo('PDOCS', 'OrderAcknowledgment', 'True');
		this.isLoading = true;
		this.orderAckSub = this.documentService.getDocumentDetails(this.id, this.orderAckFields)
			.subscribe({
				next: data => {
					if (Object.keys(data).length === 0) {
						this.isDocumentNotFound = true;
						this.isLoading = false;
					}
					this.orderAckData = data;
					this.isLoading = false;
				},
				error: (err: unknown) => {
					console.error('error loading', err);
					this.isLoading = false;
				}
			});
	}

	getDownloadDocumentUrl(id: number, filename: string) {
		return this.documentService.getDownloadDocumentUrl(id, filename);
	}

	trackDownload(id: number) {
		// eslint-disable-next-line @stylistic/max-len
		this.dbAnalyticsService.trackAction('DOWNLOAD_PDF', { documentType: 'ORDER_ACKNOWLEDGMENT', documentId: String(id) });
	}

	getDocumentURL(id: number, filename: string) {
		return this.documentService.getViewDocumentUrl(id, filename);
	}

	getDimension(documentLine: DocumentLine) {
		if (documentLine.thickness && documentLine.width && documentLine.length) {
			return `${ documentLine.thickness } * ${ documentLine.width } * ${ documentLine.length }`;
		} else if (documentLine.thickness && documentLine.width) {
			return `${ documentLine.thickness } * ${ documentLine.width }`;
		}
		return UNDERSCORE;
	}

	getMillOrderNumber(documentLine: DocumentLine) {
		if (documentLine.millOrderReference && documentLine.millOrderReferenceItem) {
			return `${ documentLine.millOrderReference }/${ documentLine.millOrderReferenceItem }`;
		} else if (documentLine.millOrderReference && !documentLine.millOrderReferenceItem) {
			return documentLine.millOrderReference;
		}
		return UNDERSCORE;
	}

	getOrderReference(document: Document) {
		return document.customerOrderReference || UNDERSCORE;
	}

	getOrderNumber(documentline: DocumentLine) {
		if (this.orderAckData?.customerOrderReference && documentline.customerOrderItemReference) {
			return `${ this.orderAckData.customerOrderReference }/${ documentline.customerOrderItemReference }`;
		} else if (this.orderAckData?.customerOrderReference && !documentline.customerOrderItemReference) {
			return this.orderAckData.customerOrderReference;
		}
		return UNDERSCORE;
	}

	getProduct(documentLine: DocumentLine) {
		return documentLine.steelGrade || UNDERSCORE;
	}

	ngOnDestroy() {
		this.orderAckSub.unsubscribe();
	}
}
